import { useEffect, useRef, useState } from 'react';

import { useVhAdminStore, VH_ADMIN_PAGE_SIZE } from '@/admin/admin.store';
import type { VhAdminUser } from '@/admin/admin.types';
import { initials, roleLabel, userContact, userRoles } from '@/admin/admin.users';
import { AdminBadge } from '@/admin/components/AdminBadge';
import { AdminCard, AdminCardList } from '@/admin/components/AdminCard';
import { AdminPagination } from '@/admin/components/AdminPagination';
import { AdminSearchInput } from '@/admin/components/AdminSearchInput';
import { AdminSelect } from '@/admin/components/AdminSelect';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import {
  AdminTable,
  AdminTableCell,
  AdminTableHead,
  AdminTableHeadCell,
  AdminTableRow,
} from '@/admin/components/AdminTable';
import styles from '@/admin/pages/AllUsersPage.module.scss';
import { UserDetailsPanel } from '@/admin/pages/UserDetailsPanel';
import { imageVariantLink } from '@/common/media';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast';

const ROLE_OPTIONS = Object.values(UserRole);

const userEmail = (user: VhAdminUser) => userContact(user, 'email');

/**
 * Which named audience a person belongs to. This replaces the old beta-tester switch:
 * that flag is now derived from the audience, so a toggle could only ever pick between
 * the two built-in ones.
 */
const AudiencePicker = ({ user }: { user: VhAdminUser }) => {
  const audiences = useVhAdminStore((state) => state.audiences);
  const setUserAudience = useVhAdminStore((state) => state.setUserAudience);
  const { first_name, last_name } = user.person_info;

  const assign = async (audienceId: string) => {
    try {
      await setUserAudience(user.id, audienceId);
    } catch {
      toast.error("Couldn't change the audience. Try again");
    }
  };

  return (
    <AdminSelect
      value={user.beta_group_id}
      onChange={assign}
      label={`Audience for ${first_name} ${last_name}`}
    >
      {audiences.map((audience) => (
        <option key={audience.id} value={audience.id}>
          {audience.name}
        </option>
      ))}
    </AdminSelect>
  );
};

const UserIdentity = ({ user }: { user: VhAdminUser }) => {
  const { first_name, last_name } = user.person_info;

  return (
    <div className={styles.user}>
      {user.avatar ? (
        <AuthorizedImage className={styles.avatar} src={imageVariantLink(user.avatar, 28)} alt="" />
      ) : (
        <span className={styles.avatarFallback}>{initials(first_name, last_name)}</span>
      )}
      <span className={styles.name}>
        {first_name} {last_name}
      </span>
    </div>
  );
};

const UserRoleBadges = ({ user }: { user: VhAdminUser }) => {
  const roles = userRoles(user);

  if (roles.length === 0) return <span className={styles.muted}>-</span>;

  return (
    <div className={styles.badges}>
      {roles.map((r) => (
        <AdminBadge key={r}>{roleLabel(r)}</AdminBadge>
      ))}
    </div>
  );
};

export const AllUsersPage = () => {
  const { users, usersCount, usersPage, usersLoading, audiences, fetchUsers, fetchAudiences } =
    useVhAdminStore();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [audience, setAudience] = useState('');
  // Held by id, so the panel follows the row through an audience change or a refetch.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const selectedUser = users.find((user) => user.id === selectedUserId) ?? null;
  const isFirstRender = useRef(true);

  useEffect(() => {
    fetchAudiences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchUsers({ page: 1, search, role, audience });
      return;
    }

    const timeout = setTimeout(() => {
      fetchUsers({ page: 1, search, role, audience });
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, role, audience]);

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>All Users</h1>
        <div className={styles.filters}>
          <AdminSelect value={role} onChange={setRole} label="Filter by role">
            <option value="">All roles</option>
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </AdminSelect>
          <AdminSelect value={audience} onChange={setAudience} label="Filter by audience">
            <option value="">All audiences</option>
            {audiences.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </AdminSelect>
          <AdminSearchInput value={search} onChange={setSearch} placeholder="Search by name…" />
        </div>
      </div>

      {usersLoading && users.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : users.length === 0 ? (
        <div className={styles.empty}>No users found</div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>User</AdminTableHeadCell>
                <AdminTableHeadCell>Contact</AdminTableHeadCell>
                <AdminTableHeadCell>Role</AdminTableHeadCell>
                <AdminTableHeadCell>Audience</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {users.map((user) => (
                  <AdminTableRow key={user.id} onActivate={() => setSelectedUserId(user.id)}>
                    <AdminTableCell>
                      <UserIdentity user={user} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <span className={styles.muted}>{userEmail(user) ?? '-'}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <UserRoleBadges user={user} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <AudiencePicker user={user} />
                    </AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {users.map((user) => (
                <AdminCard key={user.id} onActivate={() => setSelectedUserId(user.id)}>
                  <div className={styles.cardHeader}>
                    <UserIdentity user={user} />
                    <AudiencePicker user={user} />
                  </div>
                  <div className={styles.cardContact}>{userEmail(user) ?? '-'}</div>
                  <UserRoleBadges user={user} />
                </AdminCard>
              ))}
            </AdminCardList>
          </div>

          <AdminPagination
            page={usersPage}
            pageSize={VH_ADMIN_PAGE_SIZE}
            total={usersCount}
            onPageChange={(page) => fetchUsers({ page })}
          />
        </>
      )}

      <UserDetailsPanel user={selectedUser} onClose={() => setSelectedUserId(null)} />
    </div>
  );
};
